import { Link } from 'react-router-dom';
import type { Invoice } from '../../api/types.js';
import { StateBadge } from './StateBadge.js';
import { formatAmount, formatDueChip } from './format.js';
import { resolveDueDate } from './dueDate.js';

const DIRECTION_LABEL_FR: Record<Invoice['direction'], string> = {
  INBOUND: 'Reçue',
  OUTBOUND: 'Émise',
  OFF_PLATFORM: 'Hors plateforme',
};

interface Props {
  invoice: Invoice;
  tenantId: string;
  // When true, the row renders with the `is-selected` modifier — used
  // by the split-pane invoice list (REQ-FE-029a) to highlight the row
  // whose detail is currently shown in the centre pane.
  selected?: boolean;
}

// REQ-FE-031 — Row anatomy matches mockup `03-invoice-list.png`:
//
//   ┌──────────────────────────────────────────────────────────────┐
//   │  [supplier name, bold]                  [amount, right-bold] │
//   │  <invoice-id> · PO-<order-id>           [due-date chip]      │
//   │  [STATUS PILL]                                               │
//   └──────────────────────────────────────────────────────────────┘
//
// Direction is encoded into the row class (`is-outbound`,
// `is-off-platform`) so the visual filter pills can colour-code it
// without an extra glyph in the row. The 7-column table grid used
// pre-FE-031 is gone — the new layout is a 2-column 3-row card with
// the status pill spanning the full width.
export function InvoiceRow({ invoice, tenantId, selected = false }: Props) {
  const senderName = invoice.sender?.name ?? '—';
  const amount = formatAmount(invoice.amountTotal, invoice.currency);
  // REQ-FE-037 item 10: render the chip even when the due date is
  // estimated (LME-60 fallback). EST. suffix differentiates it from
  // an authoritative due date supplied by the structured invoice.
  const resolved = resolveDueDate(invoice.dueDate, invoice.invoiceDate);
  const due = formatDueChip(resolved.iso, new Date(), resolved.isEstimated);
  const href = `/t/${tenantId}/invoices/${encodeURIComponent(invoice.primaryKey)}`;
  // REQ-FE-031: mockup shows `<invoice-id> · PO-<order-id>` as the
  // sub-line. PO reference isn't on the Invoice contract today — flag
  // a future BE field via REQ-BE-N if business cares. For now we
  // render just the invoice number.
  const poRef: string | null = null;

  return (
    <Link
      to={href}
      replace
      className={[
        'invoice-row',
        `invoice-row--${invoice.direction.toLowerCase().replace('_', '-')}`,
        selected ? 'invoice-row--selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={`invoice-row-${invoice.primaryKey}`}
      aria-current={selected ? 'page' : undefined}
      title={DIRECTION_LABEL_FR[invoice.direction]}
    >
      <div className="invoice-row__top">
        <span className="invoice-row__sender" title={senderName}>
          {senderName}
        </span>
        <span className="invoice-row__amount">{amount}</span>
      </div>
      <div className="invoice-row__mid">
        <span className="invoice-row__refs">
          <span className="invoice-row__number">{invoice.invoiceNumber ?? '—'}</span>
          {poRef ? (
            <>
              <span className="invoice-row__sep" aria-hidden="true">
                ·
              </span>
              <span className="invoice-row__po">{poRef}</span>
            </>
          ) : null}
        </span>
        {due.tone !== 'unknown' ? (
          <span
            className={`invoice-row__due invoice-row__due--${due.tone}`}
            data-testid={`invoice-row-${invoice.primaryKey}-due`}
          >
            {due.label}
          </span>
        ) : null}
      </div>
      <div className="invoice-row__bot">
        <StateBadge state={invoice.state} dispute={invoice.dispute} />
      </div>
    </Link>
  );
}
